























































































































import { computed, ref, defineComponent, PropType, onMounted, nextTick } from '@vue/composition-api';
import { Message } from '@/layout/type';
import dayjs from 'dayjs';
import reportSolidSvg from '@/assets/images/health-report-solid.png';
import missionSolidSvg from '@/assets/images/health-mission-solid.svg';
import planSolidSvg from '@/assets/images/health-plan-solid.png';
import recordSolidSvg from '@/assets/images/record-solid.svg';
import showdown from 'showdown';
import { AIFunctionType, PatientDetailSourceType, TabType } from '@/const/business';

export default defineComponent({
  name: 'MenuItem',
  props: {
    message: {
      type: Object as PropType<Message>,
      default: () => ({
        timestamp: 0,
        role: 'patient',
        text: '',
        usage: AIFunctionType.healthReport,
      }),
    },
    patientId: {
      type: String,
      default: '',
    },
    isTyping: Boolean,
    isLastOne: Boolean,
    aiModelType: String,
  },

  setup(props, { emit, root }) {
    const title = computed(() => {
      if (props.message.role === 'patient') return '患者';
      if (props.message.role === 'AI') return '智能助手';
      return '我';
    });
    const isRegenerateDisabled = computed(() => {
      const { isTyping, isLastOne, message } = props;
      return !!(isTyping || !isLastOne || message?.type);
    });
    const regenerate = () => {
      emit('regenerate', props.message.aiTemplateId);
    };

    const isQuoteExpand = ref(false);

    const citationExtension = () => ({
      type: 'lang',
      regex: /\[([\d,]+)\]\(@ref\)/g, // 匹配[1,4,5,6](@ref)格式的多引用标记
      replace: (match, ids) => ids
        .split(',')
        .map((id) => {
          const quoteItem = props.message.quote?.find(q => q.index === Number(id)); // 通过index匹配引用项
          let info = '';
          if (quoteItem?.reportId) {
            info = quoteItem
              ? `${quoteItem.secondaryType || quoteItem.primaryType}\n${quoteItem.checkTime || ''}` // 换行组合信息
              : '';
          } else if (quoteItem?.followUpId) {
            info = `${quoteItem.followUpName}-${quoteItem.childPlanName}\n${quoteItem.questionnaireTime || ''}`;
          } else if (quoteItem?.refType === 'baseInfo') {
            info = '个人健康档案数据';
          }
          return `<sup 
              class="citation-ref" 
              data-cite-id="${quoteItem?.index || ''}"
              data-report-info="${info}"
            >${id}</sup>`;
        })
        .join(''),
    });

    const converter = new showdown.Converter({
      simpleLineBreaks: true, // 启用简单换行符转换
      tables: true, // 启用表格支持
      extensions: [citationExtension()], // 启用引用扩展
    });

    const isClearClicked = ref(false);
    const handleClearClick = () => {
      isClearClicked.value = true;
      emit('clearContextClick');
    };

    const addClassToLastElement = (htmlString) => {
      if (!props.isTyping) {
        return htmlString;
      }
      // 匹配起始标签
      const regex = /<([a-zA-Z]+[0-9]?[a-zA-Z]*)([^>]*)>/g;
      let match;
      let lastMatch: RegExpExecArray | null = null;
      let lastIndex = 0;
      while ((match = regex.exec(htmlString)) !== null) {
        lastMatch = match;
        lastIndex = regex.lastIndex;
      }
      if (lastMatch) {
        const tagName = lastMatch[1];
        const attributes = lastMatch[2];
        // 构建新的起始标签，添加 class 属性
        const newStartTag = `<${tagName} ${attributes} class="loading-dot">`;
        // 替换原起始标签
        const newHtmlString = htmlString.slice(0, lastIndex - lastMatch[0].length) + newStartTag + htmlString.slice(lastIndex);
        return newHtmlString;
      }
      return htmlString;
    };

    const wrapTextWithSpan = (html) => {
      // 正则表达式匹配没有标签包裹的文本
      const pattern = />([^<]+)</g;

      // 执行替换
      const modifiedHtml = html?.replace(pattern, (match, p1) => {
        // 去除前后的空白字符
        const trimmedText = p1.trim();
        // 如果文本不为空且不在标签内，则用<div>包裹
        if (trimmedText) {
          return `><div style="display: inline; line-height: 22px;">${trimmedText}</div><`;
        }
        return match;
      });

      return modifiedHtml;
    };

    // 为表格添加容器包装
    const wrapTablesWithContainer = html =>
      // 匹配表格标签并为其添加容器
      html.replace(/<table([^>]*)>/g, '<div class="table-container"><table$1>').replace(/<\/table>/g, '</table></div>')
    ;

    const renderedMarkdown = computed(() => {
      const rawHtml = converter.makeHtml(props.message.text);
      const htmlWithTableContainers = wrapTablesWithContainer(rawHtml);
      return addClassToLastElement(wrapTextWithSpan(htmlWithTableContainers));
    });

    const messageContent = ref<any>(null);

    const copyTextToClipboard = async () => {
      try {
        // 获取要复制的文本，正则过滤引用导致的多余换行和数字引用
        const text = messageContent.value.innerText.replace(/\n?\d+\n/g, '');
        // 使用 Clipboard API 复制文本
        await navigator.clipboard.writeText(text);
        emit('copySuccess');

        // 复制成功提示，3秒后清除
        setTimeout(() => {
          console.log('复制成功');
        }, 1000);
      } catch (err) {
        console.error('复制失败:', err);
      }
    };

    const toolbar = [
      {
        name: '健康报告',
        value: AIFunctionType.healthReport,
        activeIcon: reportSolidSvg,
      },
      {
        name: '健康宣教',
        value: AIFunctionType.healthEducation,
        activeIcon: missionSolidSvg,
      },
      {
        name: '健康计划',
        value: AIFunctionType.healthPlan,
        activeIcon: planSolidSvg,
      },
      {
        name: '档案智问',
        value: AIFunctionType.healthRecord,
        activeIcon: recordSolidSvg,
      },
    ];

    const currentUsage = computed(() => toolbar.find(item => item.value === props.message.usage));

    const onCopyToDialog = async () => {};

    const questionClick = (question) => {
      emit('questionClick', question);
    };
    const handleQuoteClick = (quote) => {
      const { reportId, followUpId, childPlanId, planDetailUuid, refType } = quote;
      const patientId = props.message.patientId || props.patientId;
      if (refType === 'baseInfo') {
        root.$router.push({
          path: '/default/detail',
          query: {
            origin: PatientDetailSourceType.dataPlatform,
            currentTab: String(TabType.userPortrait),
            patientId,
          },
        });
      } else if (reportId) {
        root.$router.push({
          path: '/default/detail',
          query: {
            origin: PatientDetailSourceType.dataPlatform,
            currentTab: String(TabType.checkReport),
            reportId,
            patientId,
          },
        });
      } else if (followUpId) {
        root.$router.push({
          path: '/default/detail',
          query: {
            origin: PatientDetailSourceType.dataPlatform,
            currentTab: String(TabType.timeLine),
            followUpId,
            childPlanId,
            patientId,
            endPlanDetailUUId: planDetailUuid,
            typeName: 'anchorTarget',
          },
        });
      }
    };

    const updateTooltipPosition = (event) => {
      console.log('updateTooltipPosition');

      const element = event.currentTarget;
      const rect = element.getBoundingClientRect();
      const parentRect = element.parentElement.getBoundingClientRect();
      const tooltipWidth = 120; // 假设 tooltip 宽度为 120px
      const left = rect.left - parentRect.left;
      const right = parentRect.right - rect.right;
      let tooltipLeft = '50%';

      if (left < tooltipWidth / 2) {
        tooltipLeft = '25px';
      } else if (right < tooltipWidth / 2) {
        tooltipLeft = '-10px';
      }

      element.style.setProperty('--tooltip-left', tooltipLeft);
    };

    onMounted(async () => {
      await nextTick();

      messageContent.value?.addEventListener('click', (e) => {
        const citeEl = e.target.closest('.citation-ref');
        if (citeEl) {
          const id = citeEl.dataset.citeId;
          console.log('点击', id);
          const quote = props.message.quote?.find(item => item.index === Number(id));
          handleQuoteClick(quote);
        }
      });

      messageContent.value?.addEventListener(
        'mouseenter',
        (e) => {
          const citeEl = e.target.closest('.citation-ref');
          if (citeEl) {
            updateTooltipPosition({ currentTarget: citeEl });
          }
        },
        true,
      );
    });

    return {
      dayjs,
      title,
      isRegenerateDisabled,
      renderedMarkdown,
      currentUsage,
      messageContent,
      isQuoteExpand,
      isClearClicked,
      AIFunctionType,
      regenerate,
      copyTextToClipboard,
      onCopyToDialog,
      questionClick,
      handleClearClick,
      handleQuoteClick,
    };
  },
});
